import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Image from '../../../components/AppImage';


const APIIntegrationShowcase = () => {
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);

  const apiEndpoints = [
  {
    id: 'policies',
    method: 'GET',
    endpoint: '/api/v1/policies',
    description: 'Retrieve policy changes with filtering options',
    parameters: ['category', 'payer', 'dateRange', 'impact'],
    responseTime: '120ms',
    rateLimit: '1000/hour'
  },
  {
    id: 'alerts',
    method: 'POST',
    endpoint: '/api/v1/alerts/subscribe',
    description: 'Configure custom alert subscriptions',
    parameters: ['deviceCategories', 'payerNetworks', 'notificationChannels'],
    responseTime: '85ms',
    rateLimit: '500/hour'
  },
  {
    id: 'policy-report',
    method: 'GET',
    endpoint: '/api/v1/policy/report',
    description: 'Generate policy impact reports',
    parameters: ['productIds', 'timeframe', 'format'],
    responseTime: '340ms',
    rateLimit: '200/hour'
  },
  {
    id: 'search',
    method: 'GET',
    endpoint: '/api/v1/search',
    description: 'Advanced policy search with full-text capabilities',
    parameters: ['query', 'filters', 'sortBy', 'limit'],
    responseTime: '95ms',
    rateLimit: '2000/hour'
  }];


  const integrationPartners = [
  {
    id: 1,
    name: "Salesforce Health Cloud",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6fca330-1768589932626.png",
    logoAlt: "Salesforce Health Cloud logo displayed on modern digital screen with blue gradient background and cloud computing icons",
    description: "Seamless CRM integration for policy tracking",
    status: "active",
    category: "CRM"
  },
  {
    id: 2,
    name: "Microsoft Dynamics 365",
    logo: "https://images.unsplash.com/photo-1669702895978-2e6013a00180",
    logoAlt: "Microsoft Dynamics 365 application interface shown on smartphone screen with modern office workspace background",
    description: "Enterprise resource planning integration",
    status: "active",
    category: "ERP"
  },
  {
    id: 3,
    name: "Veeva Vault",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18ae7b775-1764846857062.png",
    logoAlt: "Veeva Vault cloud platform visualization with secure vault icon and data management interface on professional display",
    description: "Medical policy content management system sync",
    status: "active",
    category: "Policy Management"
  },
  {
    id: 4,
    name: "SAP S/4HANA",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3895ecc-1764656426788.png",
    logoAlt: "SAP S/4HANA business analytics dashboard showing real-time data charts and enterprise management metrics on large monitor",
    description: "Supply chain and policy workflow automation",
    status: "beta",
    category: "ERP"
  },
  {
    id: 5,
    name: "Oracle NetSuite",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c259e27f-1767991871357.png",
    logoAlt: "Oracle NetSuite cloud business software interface displayed on laptop screen in modern corporate office environment",
    description: "Financial and operational data synchronization",
    status: "active",
    category: "Finance"
  },
  {
    id: 6,
    name: "Slack",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_13daca93f-1769491842931.png",
    logoAlt: "Slack messaging application open on laptop showing team communication channels and notification alerts in workspace",
    description: "Real-time alert notifications and team collaboration",
    status: "active",
    category: "Communication"
  }];


  const codeExamples = {
    javascript: `// PolicyAlerts API Integration Example
const PolicyAlerts = require('@policyalerts/sdk');

const client = new PolicyAlerts({
  apiKey: process.env.POLICYALERTS_API_KEY,
  environment: 'production'
});

// Fetch recent policy changes
async function getPolicyUpdates() {
  try {
    const policies = await client.policies.list({
      category: 'cardiovascular',
      payer: 'medicare',
      dateRange: 'last_30_days',
      impact: ['high', 'critical']
    });
    
    console.log(\`Found \${policies.length} policy updates\`);
    return policies;
  } catch (error) {
    console.error('API Error:', error);
  }
}

// Subscribe to real-time alerts
client.alerts.subscribe({
  deviceCategories: ['cardiovascular', 'orthopedic'],
  payerNetworks: ['medicare', 'bcbs'],
  notificationChannels: ['email', 'webhook']
});`,
    python: `# PolicyAlerts Python SDK
from policyalerts import PolicyAlertsClient

client = PolicyAlertsClient(
    api_key=os.environ['POLICYALERTS_API_KEY'],
    environment='production'
)

# Fetch policy changes with filtering
def get_policy_updates():
    try:
        policies = client.policies.list(
            category='cardiovascular',
            payer='medicare',
            date_range='last_30_days',
            impact=['high', 'critical']
        )
        
        print(f"Found {len(policies)} policy updates")
        return policies
    except Exception as e:
        print(f"API Error: {e}")

# Configure alert subscriptions
client.alerts.subscribe(
    device_categories=['cardiovascular', 'orthopedic'],
    payer_networks=['medicare', 'bcbs'],
    notification_channels=['email', 'webhook']
)`,
    curl: `# PolicyAlerts REST API Examples

# Fetch policy changes
curl -X GET "https://api.policyalerts.com/v1/policies" \\
  -H "Authorization: Bearer YOUR_API_KEY"\ -H"Content-Type: application/json" \\
  -d '{
    "category": "cardiovascular",
    "payer": "medicare",
    "dateRange": "last_30_days",
    "impact": ["high", "critical"]
  }'

# Subscribe to alerts
curl -X POST "https://api.policyalerts.com/v1/alerts/subscribe" \\
  -H "Authorization: Bearer YOUR_API_KEY"\ -H"Content-Type: application/json" \\
  -d '{
    "deviceCategories": ["cardiovascular", "orthopedic"],
    "payerNetworks": ["medicare", "bcbs"],
    "notificationChannels": ["email", "webhook"]
  }'`
  };

  const [selectedLanguage, setSelectedLanguage] = useState('javascript');

  const handleEndpointToggle = (endpointId) => {
    setSelectedEndpoints((prev) =>
    prev?.includes(endpointId) ?
    prev?.filter((id) => id !== endpointId) :
    [...prev, endpointId]
    );
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'text-success bg-success/10 border-success/20',
      POST: 'text-accent bg-accent/10 border-accent/20',
      PUT: 'text-warning bg-warning/10 border-warning/20',
      DELETE: 'text-destructive bg-destructive/10 border-destructive/20'
    };
    return colors?.[method] || 'text-muted-foreground bg-muted border-border';
  };

  return (
    <section className="bg-muted py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
            API Integration & Ecosystem
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Seamlessly integrate PolicyAlerts into your existing policy monitoring workflow with our enterprise-grade API
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
          <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-6">
              Available API Endpoints
            </h3>
            <div className="space-y-4">
              {apiEndpoints?.map((endpoint) =>
              <div
                key={endpoint?.id}
                className="border border-border rounded-lg p-4 hover:border-primary/20 transition-colors">

                  <div className="flex items-start gap-3 mb-3">
                    <Checkbox
                    checked={selectedEndpoints?.includes(endpoint?.id)}
                    onChange={() => handleEndpointToggle(endpoint?.id)} />

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getMethodColor(endpoint?.method)}`}>
                          {endpoint?.method}
                        </span>
                        <code className="text-xs text-foreground bg-muted px-2 py-1 rounded font-mono">
                          {endpoint?.endpoint}
                        </code>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {endpoint?.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          {endpoint?.responseTime}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Zap" size={12} />
                          {endpoint?.rateLimit}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg md:text-xl font-semibold text-foreground">
                Code Examples
              </h3>
              <div className="flex items-center gap-2">
                {['javascript', 'python', 'curl']?.map((lang) =>
                <button
                  key={lang}
                  onClick={() => setSelectedLanguage(lang)}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  selectedLanguage === lang ?
                  'bg-primary text-primary-foreground' :
                  'bg-muted text-muted-foreground hover:bg-muted/80'}`
                  }>

                    {lang?.toUpperCase()}
                  </button>
                )}
              </div>
            </div>
            <div className="bg-foreground/5 rounded-lg p-4 overflow-x-auto">
              <pre className="text-xs md:text-sm text-foreground font-mono whitespace-pre-wrap break-words">
                {codeExamples?.[selectedLanguage]}
              </pre>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Copy"
                iconPosition="left"
                fullWidth>

                Copy Code
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
                fullWidth>

                View Full Docs
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-6">
            Integration Partners
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {integrationPartners?.map((partner) =>
            <div
              key={partner?.id}
              className="border border-border rounded-lg p-4 hover:border-primary/20 transition-colors">

                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                    <Image
                    src={partner?.logo}
                    alt={partner?.logoAlt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-foreground line-clamp-1">
                        {partner?.name}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                    partner?.status === 'active' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`
                    }>
                        {partner?.status?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {partner?.description}
                    </p>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs text-muted-foreground">
                      <Icon name="Tag" size={10} />
                      {partner?.category}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 md:mt-12 bg-accent/10 border border-accent/20 rounded-lg p-6 md:p-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Code" size={24} color="var(--color-accent)" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">
              Request API Access
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-6">
              Get sandbox credentials to test PolicyAlerts API integration in your development environment
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                variant="default"
                size="lg"
                iconName="Key"
                iconPosition="left">

                Request API Keys
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="BookOpen"
                iconPosition="left">

                View Documentation
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default APIIntegrationShowcase;